import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
} from 'react'
import { useQuery } from '@apollo/client'

import { useTranslations } from 'next-intl'
import {
  Flex,
  Text,
  InputGroup,
  Input,
  Button,
  InputLeftAddon,
  useColorModeValue,
  VStack,
  Stack,
  Box,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
} from '@chakra-ui/react'

import { CompassMiningIcon } from '@/components/icons/CompassMiningIcon'
import { formatPrice } from '@/utils/formatPrice'
import { getAllAvailableCredits, getCartQuantity } from '@/api/checkout'
import {
  isCreditAppliedVar,
  userCreditAppliedAmountVar,
} from 'apolloCache/cartCache'
import { useRouter } from 'next/router'
import { ICartNode } from '@/types/cart.interface'
import { MAX_CREDIT_ALLOWED_PER_PRODUCT } from '@/constants'
import { CheckIcon } from '@/components/icons/CheckIcon'
import { CloseIcon } from '@/components/icons/CloseIcon'

export interface CreditsDropdownRef {
  clearAppliedCreditsRef: () => void
  triggerUnappliedCreditError: () => void
}

interface IProps {
  isInvoice?: boolean
  paymentAmount: number
  resetAppliedCredits?: boolean
  setIsPayNowButtonDisabled?: (disabled: boolean) => void
  setHasUnappliedCredits?: (hasUnappliedCredits: boolean) => void
}

const CreditsDropdown = forwardRef(
  (
    {
      isInvoice,
      paymentAmount = 0,
      resetAppliedCredits,
      setHasUnappliedCredits,
    }: IProps,
    ref
  ) => {
    const [value, setValue] = React.useState<string>('')
    const [isCreditApplied, setIsCreditApplied] = React.useState<boolean>(false)
    const [showUnappliedError, setShowUnappliedError] =
      React.useState<boolean>(false)
    const [error, setError] = React.useState<string | null>(null)
    const [displayInput, setDisplayInput] = React.useState<boolean>(false)
    const [maxCredit, setMaxCredit] = React.useState<boolean>(false)
    const borderColor = useColorModeValue('gray.200', 'whiteAlpha.300')
    const alpha700 = useColorModeValue('blackAlpha.700', 'whiteAlpha.700')

    const t = useTranslations()
    const router = useRouter()

    const { data } = useQuery(getAllAvailableCredits)
    const allCredits = data?.availableCompassCredits

    const { data: cartQuantityData } = useQuery(getCartQuantity)
    const cartQuantity = cartQuantityData?.allCarts

    const handleChange = (
      val: string,
      e: React.ChangeEvent<HTMLInputElement>
    ): string | null => {
      const { value } = e.target

      setValue(value.toLocaleString())
      return null
    }

    const getMaxCreditsAllowedInCart = (): number => {
      let maxCreditsAllowedInCart = 0

      cartQuantity?.nodes?.forEach((cart: ICartNode) => {
        const { cartItemQuantity: quantity } = cart
        maxCreditsAllowedInCart += quantity * MAX_CREDIT_ALLOWED_PER_PRODUCT
      })

      return Number(maxCreditsAllowedInCart.toFixed(2))
    }

    const clearCreditsApplied = useCallback(() => {
      setIsCreditApplied(false)
      isCreditAppliedVar(false)
      setMaxCredit(false)
      setValue('')
      userCreditAppliedAmountVar(0)
      setShowUnappliedError(false)
      setError(null)
    }, [])

    useEffect(() => {
      if (resetAppliedCredits) {
        setIsCreditApplied(false)
        isCreditAppliedVar(false)
        setValue('')
        userCreditAppliedAmountVar(0)
        setError(null)
      }
    }, [resetAppliedCredits])

    // Reset values by ref
    useImperativeHandle(ref, () => ({
      clearAppliedCreditsRef: () => {
        clearCreditsApplied()
      },
      triggerUnappliedCreditError: () => {
        if (value && !isCreditApplied) {
          setShowUnappliedError(true)
        }
      },
    }))

    useEffect(() => {
      // Detect if route has changed, if so, reset the value
      router.events.on('routeChangeComplete', () => {
        clearCreditsApplied()
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.events, clearCreditsApplied])

    useEffect(() => {
      if (isCreditAppliedVar()) {
        setIsCreditApplied(true)
      } else {
        setIsCreditApplied(false)
        userCreditAppliedAmountVar(0)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userCreditAppliedAmountVar()])
    useEffect(() => {
      // Reset display input when credits are cleared
      const currentCredits = userCreditAppliedAmountVar()
      if (currentCredits === 0 && isCreditApplied) {
        setDisplayInput(false)
        setIsCreditApplied(false)
        isCreditAppliedVar(false)
        setValue('')
        setError(null)
        setMaxCredit(false)
      }
    }, [userCreditAppliedAmountVar(), isCreditApplied])

    const validatePaymentAmount = (
      value: string,
      allCredits: number,
      maxCreditsAllowed: number
    ): string | null => {
      const credit = Number(value)

      // If the user has entered a value that is greater than the total credits available
      if (credit > allCredits) {
        return t('dashboard.billing.compass-credit-amount-error')
      }
      // If the user has entered a value that is less than 0
      if (credit < 0) {
        return t('dashboard.billing.compass-credit-amount-error')
      }
      // If the user has entered a value that is not a number
      if (isNaN(credit)) {
        return t('dashboard.billing.compass-credit-amount-error')
      }
      // If the user has entered a value that is greater than the max credits allowed
      if (!isInvoice && credit > maxCreditsAllowed) {
        return t('dashboard.billing.compass-credit-max-allowed-error', {
          maxCreditsAllowed: maxCreditsAllowed.toLocaleString(),
        })
      }
      return null
    }

    useEffect(() => {
      if (value) {
        const error = validatePaymentAmount(
          value,
          allCredits,
          getMaxCreditsAllowedInCart()
        )
        if (error) {
          setError(error)
          setIsCreditApplied(false)
          isCreditAppliedVar(false)
          setMaxCredit(false)
          setValue('')
          userCreditAppliedAmountVar(Number(0))
        } else {
          setError(null)
          setShowUnappliedError(false)
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    const applyCompassCredits = () => {
      if (value && !error) {
        setIsCreditApplied(true)
        isCreditAppliedVar(true)
        setDisplayInput(false)
        if (Number(value) >= paymentAmount) {
          userCreditAppliedAmountVar(Number(paymentAmount.toLocaleString()))
          setValue(paymentAmount.toLocaleString())
          setMaxCredit(true)
        } else {
          userCreditAppliedAmountVar(Number(value.toLocaleString()))
          setValue(value.toLocaleString())
        }
      }
    }

    const applyMaxCompassCredits = () => {
      if (!isCreditApplied) {
        let valuesToCalculate = [paymentAmount, allCredits]
        if (!isInvoice) {
          valuesToCalculate = [
            getMaxCreditsAllowedInCart(),
            ...valuesToCalculate,
          ]
        }

        const maxValue = Math.min(...valuesToCalculate).toString()
        setValue(maxValue.toLocaleString())
      }
    }

    useEffect(() => {
      if (setHasUnappliedCredits) {
        setHasUnappliedCredits(!!value && !isCreditApplied)
      }
    }, [value, isCreditApplied, setHasUnappliedCredits])

    const isCheckButtonDisabled = (): boolean => {
      if (isCreditApplied) return true
      if (!value) return true
      if (error) return true
      return false
    }

    return (
      <VStack align="inherit">
        <Text
          fontSize={{ base: '16px', md: '18px' }}
          color={useColorModeValue('blackAlpha.700', 'whiteAlpha.700')}
        >
          {t('dashboard.billing.credit-balance')}
        </Text>
        <Stack direction="row" justify="space-between" align="center">
          <Flex gap="6px" align="center">
            <CompassMiningIcon />
            <Text fontSize="18px" fontWeight="500">
              {isCreditApplied
                ? formatPrice(allCredits - Number(value))
                : formatPrice(allCredits)}{' '}
            </Text>
          </Flex>
          {displayInput ? (
            <VStack align="flex-end" spacing={1}>
              <Flex justify="flex-end" align="center" gap={1.5} w="100%">
                <Popover
                  isOpen={showUnappliedError}
                  onClose={() => setShowUnappliedError(false)}
                  placement="bottom-end"
                  closeOnBlur={false}
                  offset={[35, 8]}
                >
                  <PopoverTrigger>
                    <InputGroup
                      w="full"
                      maxW="186px"
                      borderRadius="4px"
                      borderColor={showUnappliedError ? 'red.500' : borderColor}
                      justifySelf={'flex-start'}
                    >
                      <InputLeftAddon
                        background="gray.200"
                        color="black"
                        p="12px"
                      >
                        $
                      </InputLeftAddon>
                      <Input
                        borderColor={
                          showUnappliedError ? 'red.500' : borderColor
                        }
                        background="white"
                        type="text"
                        placeholder={t('placeholders.enter-amount')}
                        _placeholder={{ color: 'gray.500', fontSize: 'sm' }}
                        aria-label={t('placeholders.enter-amount')}
                        color="blackAlpha.500"
                        disabled={isCreditApplied || allCredits <= 0}
                        onChange={(e) => handleChange(value, e)}
                        pl={2}
                        data-test-id="apply-credits-input"
                        value={
                          isCreditApplied
                            ? `${formatPrice(Number(value.toLocaleString()))}`
                            : value
                        }
                      />
                    </InputGroup>
                  </PopoverTrigger>
                  <PopoverContent
                    maxW={{ base: '200px', md: '300px' }}
                    w="auto"
                  >
                    <PopoverArrow
                      ml={{
                        base: '140px',
                        md: '120px',
                        lg: '145px',
                        xl: '120px',
                      }}
                    />
                    <PopoverBody>
                      <VStack spacing={3} align="flex-end">
                        <Text fontSize="sm" textAlign="center">
                          {t('dashboard.billing.click-checkmark-to-apply')}
                        </Text>
                        <Button
                          colorScheme="blue"
                          size="sm"
                          onClick={() => setShowUnappliedError(false)}
                          w="30%"
                        >
                          {t('buttons.got-it')}
                        </Button>
                      </VStack>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
                <Box
                  bg="brand.200"
                  borderRadius="4px"
                  display={'flex'}
                  h="40px"
                  w="40px"
                  justifyContent={'center'}
                  alignItems={'center'}
                  opacity={isCheckButtonDisabled() ? 0.5 : 1}
                >
                  <CheckIcon
                    boxSize={5}
                    color="white"
                    cursor="pointer"
                    onClick={applyCompassCredits}
                    data-test-id="apply-checkmark"
                    alignSelf={'center'}
                    pointerEvents={isCheckButtonDisabled() ? 'none' : 'auto'}
                  />
                </Box>
                <CloseIcon
                  boxSize={6}
                  cursor="pointer"
                  onClick={() => {
                    setDisplayInput(false)
                    setError(null)
                    setValue('')
                    setIsCreditApplied(false)
                  }}
                />
              </Flex>
              <Text fontSize="12px" fontWeight="500" color={alpha700}>
                {t('dashboard.billing.credit-apply-limit', {
                  maxCreditPerMiner: formatPrice(
                    MAX_CREDIT_ALLOWED_PER_PRODUCT
                  ),
                })}{' '}
                <Text
                  as="span"
                  color="blue.500"
                  cursor="pointer"
                  onClick={applyMaxCompassCredits}
                  textDecoration="underline"
                >
                  {t('dashboard.billing.apply-max')}
                </Text>
              </Text>
            </VStack>
          ) : isCreditApplied ? (
            <Flex justify="end" align="center" gap={1}>
              <Text
                color="green.500"
                fontSize="18px"
                fontWeight="500"
                data-test-id="applied-text"
              >
                {`${formatPrice(value)} ${t('dashboard.billing.applied')}`}
              </Text>
              <CloseIcon
                boxSize={6}
                cursor="pointer"
                onClick={clearCreditsApplied}
              />
            </Flex>
          ) : (
            <Button
              colorScheme="blue"
              bg="brand.200"
              color="white"
              onClick={() => {
                setDisplayInput(true)
              }}
              data-test-id="apply-credits-button"
            >
              {t('dashboard.billing.compass-credit')}
            </Button>
          )}
        </Stack>
        {maxCredit && (
          <Text color="gray.500" fontStyle="italic" align="end" mt={2}>
            {t('dashboard.billing.maximum-credits-applied')}
          </Text>
        )}
      </VStack>
    )
  }
)

CreditsDropdown.displayName = 'CreditsDropdown'

export default CreditsDropdown
